import React, { useEffect, useState, useRef } from 'react';
import InfoBanner from '../components/InfoBanner';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { fetchDatabases, deleteDatabase, addDatabase, updateDatabaseStatus, updateDatabaseDataView, Database } from '../store/slices/databasesSlice';
import { fetchFlavors } from '../store/slices/flavorsSlice';
import axiosInstance from '../utility/axiosInstance';
import { useToast } from '../components/ToastContext';
import { useConfirm } from '../components/ConfirmContext';
import Select, { SingleValue } from 'react-select';
import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import Modal from '../components/Modal';
import ManagedBackupsModal from '../components/ManagedBackupsModal';
import HaOption, { HA_MEMBERS, haMemberCount } from '../components/HaOption';
import UpgradeToHaModal, { haUpgradeOption } from '../components/UpgradeToHaModal';
import DatabaseDataView from '../components/DatabaseData/DatabaseDataView';
import { dataViewOption } from '../components/DatabaseData/dataView';
import useDataViewSetupWatch from '../hooks/useDataViewSetupWatch';
import { FaPlus, FaSyncAlt, FaRegCopy, FaCheck } from 'react-icons/fa';
import TrialLock from '../components/TrialLock';
import { useLimitedTrial } from '../hooks/useLimitedTrial';
import useAutoOpenCreateModal from '../hooks/useAutoOpenCreateModal';
import '../styles/InstancesPage.scss';
import '../styles/DatabasesPage.scss';
import { platformImage, toFlavorOptions } from '../utility/flavorOptions';
import { NameField, SecretField, formatSizeOption } from '../components/CreateFormFields';
import { secretOk } from '../utility/fieldRules';
import { defaultKeypair, rememberKeypair } from '../utility/defaultKeypair';

const ITEMS_PER_PAGE = 10;

type SelectOption = { value: string; label: string; isDisabled?: boolean; price?: string | null };

const DatabasesPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { showToast } = useToast();
    const { confirm } = useConfirm();
    const { databases, status } = useSelector((s: RootState) => s.databases);
    const flavors = useSelector((s: RootState) => s.flavors.data);
    const images = useSelector((s: RootState) => s.images.data);
    const vmInstances = useSelector((s: RootState) => s.instances.data);
    const isTrial = useLimitedTrial();

    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newDb, setNewDb] = useState<{
        name: string; db_name: string; db_user: string; db_password: string;
        version: string; flavor_id: string; keypair_name: string;
    }>({ name: '', db_name: 'app', db_user: 'appuser', db_password: '', version: '16', flavor_id: '', keypair_name: '' });
    const [flavorOption, setFlavorOption] = useState<SelectOption | null>(null);
    const [keypairOption, setKeypairOption] = useState<SelectOption | null>(null);
    const [keypairs, setKeypairs] = useState<SelectOption[]>([]);
    // High availability: 3 machines behind a load balancer instead of 1, so ~3x the hourly bill.
    // The trial cannot have it (3 machines plus a load balancer is exactly what the trial tier
    // excludes), so it is forced off there rather than being sent and rejected server-side.
    const [ha, setHa] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [networkId, setNetworkId] = useState<string | null>(null);
    const [justCreated, setJustCreated] = useState<Database & { db_password: string } | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [backupsFor, setBackupsFor] = useState<Database | null>(null);
    const [upgradeFor, setUpgradeFor] = useState<Database | null>(null);
    // The database whose data view is open in place of the list. Its id, not the row: a refetch then shows the
    // current row, and a database that is gone drops the view back to the list.
    const [dataForId, setDataForId] = useState<number | null>(null);
    const pollRefs = useRef<Record<number, NodeJS.Timeout>>({});

    const creationLocked = useAutoOpenCreateModal(setShowCreate, false);

    // A single machine is marked ready below as soon as its VM is up, minutes before it reports its data view setup,
    // so its data_view is re-read until it settles (hooks/useDataViewSetupWatch.ts says why).
    const watchDataViewSetup = useDataViewSetupWatch((id, dataView) => {
        dispatch(updateDatabaseDataView({ id, data_view: dataView }));
    });

    useEffect(() => {
        dispatch(fetchDatabases());
        dispatch(fetchFlavors());
    }, [dispatch]);

    useEffect(() => {
        if (!showCreate) return;
        Promise.all([
            axiosInstance.get('/v1/keypairs'),
            axiosInstance.get('/v1/private_networks'),
        ]).then(([kpRes, netRes]) => {
            const kps = (Array.isArray(kpRes.data) ? kpRes.data : [])
                .map((k: any) => ({ value: k.keypair?.name ?? '', label: k.keypair?.name ?? '' }))
                .filter((k: SelectOption) => k.value);
            setKeypairs(kps);
            setKeypairOption(prev => prev ?? defaultKeypair(kps));
            const nets = Array.isArray(netRes.data) ? netRes.data : [];
            if (nets[0]?.id) setNetworkId(nets[0].id);
        }).catch((_e) => { /* ignore */ });
    }, [showCreate]);

    useEffect(() => {
        // Single-machine databases only. This poll flips the row to "ready" as soon as ONE VM named
        // db-<name> is ACTIVE, which is a fine signal for one machine and a wrong one for three: an
        // HA database with one member up is not ready, and PATCHing it ready here would write that
        // wrong state back to the API. HA readiness is decided by the workflow that builds it, so
        // the row updates on the next fetch instead.
        databases.filter(d => d.status === 'provisioning' && !d.ha).forEach(db => {
            if (pollRefs.current[db.id]) return;
            pollRefs.current[db.id] = setInterval(async () => {
                try {
                    const listRes = await axiosInstance.get('/v1/instances');
                    const instances = Array.isArray(listRes.data) ? listRes.data : [];
                    const inst = instances.find((i: any) => i.name === `db-${db.name}` && i.status === 'ACTIVE');
                    if (inst) {
                        const ip = inst.metadata?.floatingIp ?? null;
                        clearInterval(pollRefs.current[db.id]);
                        delete pollRefs.current[db.id];
                        const patched = await axiosInstance.patch(`/v1/databases/${db.id}`, { status: 'ready', floating_ip: ip });
                        dispatch(updateDatabaseStatus({ id: db.id, status: 'ready', floating_ip: ip }));
                        // DESIGN_V1.md D6: the answer carries data_view when the data view is on for this account.
                        // False here usually means "its setup has not reported yet", so it is watched until it settles.
                        const dataView = patched?.data?.data_view;
                        if (typeof dataView === 'boolean') dispatch(updateDatabaseDataView({ id: db.id, data_view: dataView }));
                        if (dataView === false) watchDataViewSetup(db.id);
                    }
                } catch (_e) { /* keep polling */ }
            }, 15000);
        });
        return () => {
            Object.values(pollRefs.current).forEach(clearInterval);
            // Forget them too. The guard above skips a row that still has a ref, so a stopped interval left here was
            // never started again: the first machine to turn ready ended the poll of every other one still building.
            pollRefs.current = {};
        };
    }, [databases, dispatch]);

    useEffect(() => {
        setCurrentPage(0);
    }, [databases.length]);

    const totalDatabases = databases.length;
    const totalPages = Math.ceil(totalDatabases / ITEMS_PER_PAGE);
    const paginatedDatabases = databases.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    );

    // Every number from /v1/flavors; a size the datastore image cannot boot is locked with the reason, from that image's
    // own min_ram/min_disk (/v1/images), as on the instance form. One machine's price.
    const flavorOptions: SelectOption[] = toFlavorOptions(flavors, undefined, { image: platformImage(images, 'datastore') });

    // The rate the HA cost estimate multiplies by. Same source the instance page uses: the selected
    // flavor's real hourly_rate from /v1/flavors (pricing_rules), never a hardcoded price table.
    const selectedFlavorRate: number | null = (() => {
        if (!flavorOption) return null;
        const f: any = (Array.isArray(flavors) ? flavors : []).find((x: any) => String(x.id) === flavorOption.value);
        const rate = f?.hourly_rate;
        return rate != null && Number.isFinite(Number(rate)) ? Number(rate) : null;
    })();

    // Managed Postgres is one of the few resources the no-card trial allows, but 3 machines behind a
    // load balancer is not: keep HA off there rather than letting the create fail server-side.
    const haRequested = ha && !isTrial;

    // No-card trial caps the one allowed managed DB (Postgres) at m1.small (by RAM + vCPUs vs the real
    // m1.small flavor). Larger sizes need a card.
    const dbCeiling = (Array.isArray(flavors) ? flavors : []).find((f: any) => (f.name?.toLowerCase() || '') === 'm1.small');
    const flavorAboveTrialDb = (flavorId: string): boolean => {
        if (!isTrial) return false;
        const f = (Array.isArray(flavors) ? flavors : []).find((x: any) => String(x.id) === String(flavorId));
        if (!f) return false;
        if (dbCeiling && dbCeiling.ram != null && dbCeiling.vcpus != null) {
            return Number(f.ram) > Number(dbCeiling.ram) || Number(f.vcpus) > Number(dbCeiling.vcpus);
        }
        const n = (f.name?.toLowerCase() || '');
        return n.includes('medium') || n.includes('large');
    };

    // The form opens on m1.small (Milos, 2026-10-27), the largest size the no-card trial allows, so
    // it is never a size the account cannot use; only when it is offered and not locked. Any size can
    // still be picked, and a size already picked is never overwritten.
    useEffect(() => {
        if (!showCreate || flavorOption) return;
        const small = (Array.isArray(flavors) ? flavors : []).find((f: any) => (f.name?.toLowerCase() || '') === 'm1.small');
        const opt = small ? flavorOptions.find(o => o.value === String(small.id)) : undefined;
        if (opt && !opt.isDisabled && !flavorAboveTrialDb(opt.value)) setFlavorOption(opt);
        // flavorOptions and flavorAboveTrialDb are derived from flavors, images and isTrial each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCreate, flavorOption, flavors, images, isTrial]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!flavorOption) { showToast('Select a server size', 'error'); return; }
        if (!keypairOption) { showToast('Select an SSH keypair', 'error'); return; }
        if (!networkId) { showToast('No private network found. Create one first.', 'error'); return; }
        if (!newDb.name.trim()) { showToast('Enter a name', 'error'); return; }

        setCreating(true);
        try {
            const res = await axiosInstance.post('/v1/databases', {
                name: newDb.name.trim(),
                db_name: newDb.db_name.trim() || 'app',
                db_user: newDb.db_user.trim() || 'appuser',
                db_password: newDb.db_password.trim() || undefined,
                version: newDb.version,
                flavor_id: flavorOption.value,
                network_id: networkId,
                keypair_name: keypairOption.value,
                security_group_names: ['default'],
                imageName: 'ubuntu',
                // Only sent when asked for. A create that omits the field keeps working against an
                // API build that does not know about HA yet; sending ha:false everywhere would not.
                ...(haRequested ? { ha: true } : {}),
            });
            setJustCreated(res.data);
            rememberKeypair(keypairOption.value);
            dispatch(addDatabase(res.data));
            setShowCreate(false);
            setNewDb({ name: '', db_name: 'app', db_user: 'appuser', db_password: '', version: '16', flavor_id: '', keypair_name: '' });
            setFlavorOption(null);
            setHa(false);
            showToast(haRequested
                ? `Highly available database provisioning started - ${HA_MEMBERS} machines, billed as ${HA_MEMBERS}`
                : 'Database provisioning started', 'success');
        } catch (err: any) {
            showToast(err.response?.data?.error ?? 'Failed to create database', 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (db: Database) => {
        const proceed = await confirm({
            title: 'Delete Database',
            message: `Delete "${db.name}"? This will destroy the server and all data.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;
        setDeletingId(db.id);
        try {
            await dispatch(deleteDatabase(db.id)).unwrap();
            showToast(`${db.name} deleted`, 'success');
        } catch (err: any) {
            showToast(err.message ?? 'Failed to delete', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    const handleCopyConnection = (db: Database) => {
        const str = connectionString(db);
        if (!str) return;
        const doCopy = () => {
            if (navigator.clipboard && window.isSecureContext) {
                return navigator.clipboard.writeText(str);
            }
            const el = document.createElement('textarea');
            el.value = str;
            el.style.position = 'fixed';
            el.style.opacity = '0';
            document.body.appendChild(el);
            el.focus();
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            return Promise.resolve();
        };
        doCopy().then(() => {
            setCopiedId(db.id);
            showToast('Copied - fill in the password shown when the database was created (it is not stored)', 'success');
            setTimeout(() => setCopiedId(null), 2000);
        }).catch(() => showToast('Failed to copy', 'error'));
    };

    // Managed DBs are private (see the banner) - connect from an instance on the same private
    // network using the DB's INTERNAL IP (the fixed address on the private network, the same value
    // shown as "Internal IP" on Instance Details). Falls back to the floating IP if the VM isn't loaded.
    const hostFor = (db: Database): string | null => {
        // An HA database is reached through the load balancer address, never through a member's own
        // IP: the member behind that IP is replaced whenever it is rebuilt, and a connection string
        // pinned to it would break silently at exactly the moment failover was supposed to save it.
        if (db.ha && db.vip) return db.vip;
        if (db.private_ip) return db.private_ip;
        const vm: any = (vmInstances ?? []).find((i: any) => i.id === (db as any).instance_id);
        const internalIp = vm
            ? (Object.values(vm.addresses ?? {}).flat() as any[]).find(a => a?.["OS-EXT-IPS:type"] === "fixed")?.addr
            : null;
        return internalIp ?? db.floating_ip ?? null;
    };

    const connectionString = (db: Database) => {
        const host = hostFor(db);
        return host ? `postgresql://${db.db_user}:****@${host}:${db.port}/${db.db_name}` : null;
    };

    const fullConnectionString = (db: Database & { db_password?: string }) => {
        const host = hostFor(db);
        return host ? `postgresql://${db.db_user}:${db.db_password ?? '****'}@${host}:${db.port}/${db.db_name}` : null;
    };

    // Availability, reported only as far as the API actually described it. `ha` missing entirely
    // (an older row, or a build whose SELECT does not list the column) renders nothing: claiming
    // "single machine" for a database we were told nothing about would be a guess presented as a
    // fact, and the member count is shown only when member_ips came back with members in it.
    const haBadge = (db: Database) => {
        // An upgrade in flight is its own state and gets its own colour. The row still reads
        // status='ready' throughout, correctly - the SINGLE machine is still serving - so without
        // this the console shows nothing at all for 20 minutes and offers the button again.
        if (!db.ha && db.ha_upgrade_in_progress) {
            return <span className="db-ha-upgrading-badge">Upgrading to HA…</span>;
        }
        if (!db.ha) return null;
        const members = haMemberCount(db.member_ips);
        return <span className="db-ha-badge">{members ? `HA · ${members} machines` : 'HA'}</span>;
    };
    const availabilityLabel = (db: Database): string | null => {
        if (db.ha === undefined) return null;
        if (!db.ha) return 'Single machine';
        const members = haMemberCount(db.member_ips);
        return members ? `High availability, ${members} machines` : 'High availability';
    };

    // ONE Actions menu, built once and rendered by both the table and the mobile cards. They were
    // two hand-maintained copies of the same array, which is how a new action reaches one of them
    // and not the other - and a mobile user who cannot see "Make highly available" has no other
    // way to find it.
    const actionOptions = (db: Database) => {
        // Absent when the data view is off for this account; locked, with the reason, when this database cannot use it.
        const data = dataViewOption(db);
        const upgrade = haUpgradeOption(db, isTrial);
        return [
            ...(data ? [data] : []),
            ...(upgrade ? [upgrade] : []),
            { value: 'backups', label: 'Backups' },
            { value: 'delete', label: 'Delete Database' },
        ];
    };
    // A row opens its data view, as Actions > View data does, whenever that entry is not locked (Milos 2026-10-26: "On
    // click per data row here should open that view data"). A click on a button or the Actions menu, or one that ends
    // a text selection (copying part of the connection by hand), does not.
    const canOpenData = (db: Database) => {
        const opt = dataViewOption(db);
        return !!opt && !opt.isDisabled && deletingId !== db.id;
    };
    const openFromRow = (db: Database) => (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
        if (window.getSelection()?.toString()) return;
        setDataForId(db.id);
    };
    // The name is the keyboard's way in: a button with the name as its text
    const nameOf = (db: Database) => (canOpenData(db)
        ? <button type="button" className="db-name-open" title="View data" onClick={() => setDataForId(db.id)}>{db.name}</button>
        : db.name);
    const handleAction = (db: Database, action?: string) => {
        if (action === 'delete') handleDelete(db);
        else if (action === 'backups') setBackupsFor(db);
        else if (action === 'upgrade-ha') setUpgradeFor(db);
        else if (action === 'data') setDataForId(db.id);
    };

    // The REAL hourly rate of this database's current size, so the upgrade modal can show what it
    // will actually cost rather than "about 3x". Null when the size is unknown (an older row with
    // no flavor_id, or a flavor the pricing rules do not cover), and the modal says so plainly
    // instead of inventing a figure.
    const hourlyRateFor = (db: Database): number | null => {
        const fl: any = (flavors ?? []).find((f: any) => String(f.id) === String((db as any).flavor_id));
        const rate = fl?.hourly_rate;
        return rate != null && Number.isFinite(Number(rate)) ? Number(rate) : null;
    };

    const isLoading = status === 'loading';

    // The data view, in place of the list (after every hook above, so the hook order never changes). The page stays
    // mounted, so the provisioning poll keeps running underneath it.
    const dataDb = dataForId === null ? null : databases.find(d => d.id === dataForId) ?? null;
    if (dataDb) {
        return (
            <div className="keypairs-page">
                <DatabaseDataView
                    key={dataDb.id}
                    db={dataDb}
                    onBack={() => setDataForId(null)}
                    onRefreshDatabase={() => { dispatch(fetchDatabases()); }}
                />
            </div>
        );
    }

    return (
        <div className="keypairs-page">
            <div className="page-header">
                <p className="page-subtitle">
                    Dedicated PostgreSQL servers. Ready to connect in minutes.
                </p>
                <a href="https://docs.redu.cloud/Guides/databases" target="_blank" rel="noopener noreferrer"
                   className="docs-link">
                    Documentation →
                </a>
            </div>

            <InfoBanner id="databases-private-access">
                <span>Managed databases are <strong>private</strong> - reachable only from an instance on the <strong>same private network</strong>, using the database&apos;s private IP (e.g. <code>postgresql://appuser:****@10.1.x.x:5432/app</code>). They are not exposed to the public internet.</span>
            </InfoBanner>

            <TrialLock locked={false} paidResource>
            <button className="create-button" onClick={() => setShowCreate(true)} title="New Database">
                <FaPlus />
                <span>New Database</span>
            </button>
            </TrialLock>

            {/* Just-created credentials banner - shown once */}
            {justCreated && (
                <div className="db-credentials-banner">
                    <div className="db-cred-header">
                        <span className="db-cred-title">✓ <strong>{justCreated.name}</strong> is provisioning</span>
                        <button className="db-cred-dismiss" onClick={() => setJustCreated(null)}>✕</button>
                    </div>
                    <p className="db-cred-note">Save these credentials now - the password won&apos;t be shown again.</p>
                    <div className="db-cred-grid">
                        {availabilityLabel(justCreated) && (
                            <div className="db-cred-row"><span>Availability</span><code>{availabilityLabel(justCreated)}</code></div>
                        )}
                        <div className="db-cred-row"><span>Host</span><code>{hostFor(justCreated) ?? 'Provisioning… check back in 5 min'}</code></div>
                        <div className="db-cred-row"><span>Port</span><code>5432</code></div>
                        <div className="db-cred-row"><span>Database</span><code>{justCreated.db_name}</code></div>
                        <div className="db-cred-row"><span>User</span><code>{justCreated.db_user}</code></div>
                        <div className="db-cred-row"><span>Password</span><code>{justCreated.db_password}</code></div>
                        {fullConnectionString(justCreated) && (
                            <div className="db-cred-row">
                                <span>Connection string</span>
                                <code className="db-cred-conn" onClick={() => { navigator.clipboard?.writeText(fullConnectionString(justCreated)!); showToast('Copied', 'success'); }}>
                                    {fullConnectionString(justCreated)} <span className="copy-hint">click to copy</span>
                                </code>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Desktop table */}
            <div className="desktop-view">
                <div className="table-container">
                    <table className="keypairs-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>PostgreSQL</th>
                                <th>Database</th>
                                <th>Connection</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="loading-state">
                                        <FaSyncAlt className="spin" /> Loading databases...
                                    </td>
                                </tr>
                            ) : paginatedDatabases.length > 0 ? (
                                paginatedDatabases.map(db => {
                                    const conn = connectionString(db);
                                    return (
                                    <tr key={db.id} className={canOpenData(db) ? 'db-row-open' : undefined} onClick={canOpenData(db) ? openFromRow(db) : undefined}>
                                        <td className="keypair-name">{nameOf(db)}{haBadge(db)}</td>
                                        <td>v{db.db_version}</td>
                                        <td><code>{db.db_name}</code></td>
                                        <td className="db-conn">
                                            {conn ? (
                                                <div className="db-conn-wrapper">
                                                    <code>{conn}</code>
                                                    <button
                                                        className={`db-copy-btn${copiedId === db.id ? ' copied' : ''}`}
                                                        onClick={() => handleCopyConnection(db)}
                                                        title="Copy connection string"
                                                    >
                                                        {copiedId === db.id ? <FaCheck /> : <FaRegCopy />}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="db-provisioning">Provisioning…</span>
                                            )}
                                        </td>
                                        <td><span className={`db-status db-status-${deletingId === db.id ? 'provisioning' : db.status}`}>{deletingId === db.id ? 'deleting…' : db.status}</span></td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <Select
                                                options={actionOptions(db)}
                                                onChange={opt => handleAction(db, opt?.value)}
                                                placeholder="Actions"
                                                isSearchable={false}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={getActionSelectStyles()}
                                            />
                                        </td>
                                    </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={6} className="empty-state">No databases yet</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                            <button
                                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                disabled={currentPage === 0}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >← Prev</button>
                            <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalDatabases)} of {totalDatabases}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={currentPage >= totalPages - 1}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >Next →</button>
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile cards */}
            <div className="mobile-view">
                {isLoading ? (
                    <div className="loading-state">
                        <FaSyncAlt className="spin" />
                        <p>Loading databases...</p>
                    </div>
                ) : (
                    <div className="cards-container">
                        {paginatedDatabases.length > 0 ? paginatedDatabases.map(db => {
                            const conn = connectionString(db);
                            return (
                            <div key={db.id} className={`keypair-card${canOpenData(db) ? ' db-row-open' : ''}`} onClick={canOpenData(db) ? openFromRow(db) : undefined}>
                                <div className="card-header">
                                    <h3 className="card-title">{nameOf(db)}</h3>
                                    <div style={{ width: '100%', marginTop: 12 }} onClick={e => e.stopPropagation()}>
                                        <Select
                                            options={actionOptions(db)}
                                            onChange={opt => handleAction(db, opt?.value)}
                                            placeholder="Actions"
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            styles={getActionSelectStyles()}
                                        />
                                    </div>
                                </div>
                                <div className="card-content">
                                    <div className="fingerprint"><span className="label">PostgreSQL:</span><span className="value">v{db.db_version}</span></div>
                                    <div className="fingerprint"><span className="label">Database:</span><span className="value">{db.db_name}</span></div>
                                    {availabilityLabel(db) && (
                                        <div className="fingerprint"><span className="label">Availability:</span><span className="value">{availabilityLabel(db)}</span></div>
                                    )}
                                    <div className="fingerprint"><span className="label">Status:</span><span className="value"><span className={`db-status db-status-${deletingId === db.id ? 'provisioning' : db.status}`}>{deletingId === db.id ? 'deleting…' : db.status}</span></span></div>
                                    <div className="fingerprint">
                                        <span className="label">Connection:</span>
                                        <span className="value db-conn">
                                            {conn ? (
                                                <span className="db-conn-wrapper">
                                                    <span>{conn}</span>
                                                    <button
                                                        className={`db-copy-btn${copiedId === db.id ? ' copied' : ''}`}
                                                        onClick={() => handleCopyConnection(db)}
                                                        title="Copy connection string"
                                                    >
                                                        {copiedId === db.id ? <FaCheck /> : <FaRegCopy />}
                                                    </button>
                                                </span>
                                            ) : 'Provisioning…'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            );
                        }) : (
                            <div className="empty-state"><p>No databases yet</p></div>
                        )}
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px' }}>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                    disabled={currentPage === 0}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >← Prev</button>
                                <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                    {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalDatabases)} of {totalDatabases}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={currentPage >= totalPages - 1}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >Next →</button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Backups modal */}
            <ManagedBackupsModal
                isOpen={!!backupsFor}
                onClose={() => setBackupsFor(null)}
                apiBase="/v1/databases"
                serviceId={backupsFor?.id ?? null}
                serviceName={backupsFor?.name ?? ''}
            />

            {/* Upgrade a live single machine to high availability, in place */}
            <UpgradeToHaModal
                isOpen={!!upgradeFor}
                onClose={() => setUpgradeFor(null)}
                target={upgradeFor ? {
                    id: upgradeFor.id,
                    name: upgradeFor.name,
                    serviceLabel: 'PostgreSQL',
                    apiBase: '/v1/databases',
                    hourlyRate: hourlyRateFor(upgradeFor),
                } : null}
                // Refetch rather than patch the row locally: the upgrade's visible state is
                // computed server-side (with the staleness bound), so guessing it here would be a
                // second, divergent copy of the same rule.
                onStarted={() => { dispatch(fetchDatabases()); }}
            />

            {/* Create modal */}
            <Modal isOpen={showCreate && !creationLocked} onClose={() => setShowCreate(false)} title="New PostgreSQL Database">
                <form onSubmit={handleCreate} className="create-form db-create-form">
                    <NameField value={newDb.name} onChange={v => setNewDb(p => ({ ...p, name: v }))} placeholder="my-database" />
                    <div className="db-create-pair">
                        <div className="form-group">
                            <label>Database name</label>
                            <input value={newDb.db_name} onChange={e => setNewDb(p => ({ ...p, db_name: e.target.value }))} placeholder="app" />
                        </div>
                        <div className="form-group">
                            <label>Username</label>
                            <input value={newDb.db_user} onChange={e => setNewDb(p => ({ ...p, db_user: e.target.value }))} placeholder="appuser" />
                        </div>
                    </div>
                    <div className="db-create-pair">
                        <SecretField label="Password" value={newDb.db_password} onChange={v => setNewDb(p => ({ ...p, db_password: v }))} />
                        <div className="form-group">
                            <label>Version</label>
                            <Select
                                options={[{ value: '16', label: '16 (recommended)' }, { value: '15', label: '15' }, { value: '14', label: '14' }]}
                                value={{ value: newDb.version, label: `${newDb.version}${newDb.version === '16' ? ' (recommended)' : ''}` }}
                                onChange={(v: SingleValue<SelectOption>) => setNewDb(p => ({ ...p, version: v?.value ?? '16' }))}
                                styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed"
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Server size <span style={{ color: 'var(--color-error,#e53e3e)' }}>*</span></label>
                        <Select
                            options={flavorOptions} value={flavorOption}
                            onChange={(v: SingleValue<SelectOption>) => setFlavorOption(v)}
                            formatOptionLabel={formatSizeOption}
                            isOptionDisabled={(opt: SelectOption) => !!opt.isDisabled || flavorAboveTrialDb(opt.value)}
                            placeholder="Select server size…" styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed"
                        />
                        {isTrial && (
                            <p className="help-text">Trial: up to m1.small. Add a card for larger sizes.</p>
                        )}
                    </div>
                    <div className="form-group">
                        <label>SSH keypair <span style={{ color: 'var(--color-error,#e53e3e)' }}>*</span></label>
                        <Select
                            options={keypairs} value={keypairOption}
                            onChange={(v: SingleValue<SelectOption>) => setKeypairOption(v)}
                            placeholder="Select keypair…" styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed"
                        />
                    </div>
                    {/* Directly above the summary and the submit button, so the 3x cost is the last
                        thing read before Create is pressed. */}
                    <HaOption
                        enabled={haRequested}
                        onChange={setHa}
                        hourlyRate={selectedFlavorRate}
                        serviceLabel="PostgreSQL"
                        disabledReason={isTrial
                            ? 'High availability needs a card: it runs 3 machines behind a load balancer, which the limited trial does not cover.'
                            : null}
                    />
                    <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setShowCreate(false)}>Cancel</button>
                        <button type="submit" className="submit-button" disabled={creating || !flavorOption || !keypairOption || !newDb.name || !secretOk(newDb.db_password)}>
                            {creating ? 'Creating…' : haRequested ? `Create Database (${HA_MEMBERS} machines)` : 'Create Database'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default DatabasesPage;
